import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Send, User, Clock, CheckCheck, AlertCircle, Loader
} from 'lucide-react';
import chatService from '../../services/chatService';

const ChatConsultation = () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  const [consultation, setConsultation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [chatSettings, setChatSettings] = useState(null);
  const [endingConsultation, setEndingConsultation] = useState(false);

  // Polling interval for new messages
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    loadConsultationData();
    startPolling();

    return () => {
      stopPolling();
    };
  }, [consultationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startPolling = () => {
    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    // Poll for new messages every 5 seconds (reduced frequency)
    pollingIntervalRef.current = setInterval(() => {
      fetchMessages(false);
    }, 5000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const loadConsultationData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchConsultationInfo(),
        fetchMessages(true)
      ]);
      setError(null);
    } catch (err) {
      console.error('Error loading consultation:', err);
      setError(err.error || 'Failed to load consultation');
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultationInfo = async () => {
    try {
      const response = await chatService.getConsultationInfo(consultationId);
      if (response.success) {
        setConsultation(response.consultation);
        setChatSettings(response.chat_settings);
      }
    } catch (err) {
      throw err;
    }
  };

  const fetchMessages = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const response = await chatService.getConsultationMessages(consultationId);
      if (response.success) {
        setMessages(response.messages || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);

      // Stop polling if chat is not allowed (403 error)
      if (err.error && err.error.includes('not allowed')) {
        console.log('Chat not allowed at this time, stopping polling');
        stopPolling();
      }

      if (showLoading) {
        setError(err.error || 'Failed to load messages');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || sending) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      const response = await chatService.sendMessage(consultationId, messageText);
      if (response.success) {
        console.log('Message sent successfully, fetching latest messages...');
        // Wait a brief moment for the message to be processed on the server
        setTimeout(async () => {
          try {
            // Skip cache to get fresh messages after sending
            const messagesResponse = await chatService.getConsultationMessages(consultationId, 1, 50, true);
            if (messagesResponse.success) {
              console.log('Fetched messages:', messagesResponse.messages?.length || 0);
              setMessages(messagesResponse.messages || []);
            }
          } catch (fetchErr) {
            console.error('Error fetching messages after send:', fetchErr);
            // Even if fetch fails, restart polling to get messages
          }
        }, 500); // Wait 500ms for server to process the message
        // Restart polling in case it was stopped due to time restrictions
        startPolling();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.error || 'Failed to send message');
      // Restore message if send failed
      setNewMessage(messageText);
    } finally {
      setSending(false);
      messageInputRef.current?.focus();
    }
  };

  const handleEndConsultation = async () => {
    if (!window.confirm('Are you sure you want to end this consultation?')) {
      return;
    }

    try {
      setEndingConsultation(true);
      const response = await chatService.endConsultation(consultationId);
      if (response.success) {
        alert('Consultation ended successfully');
        navigate('/chat-consultations');
      }
    } catch (err) {
      console.error('Error ending consultation:', err);
      alert(err.error || 'Failed to end consultation');
    } finally {
      setEndingConsultation(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatMessageDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
  };

  const groupMessagesByDate = () => {
    const groups = {};
    messages.forEach(msg => {
      const dateKey = formatMessageDate(msg.timestamp);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(msg);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading consultation...</p>
        </div>
      </div>
    );
  }

  if (error && !consultation) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/chat-consultations')}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Back to Consultations
          </button>
        </div>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate();
  const isActive = consultation?.status === 'active';
  const canSendMessages = chatSettings?.is_allowed && isActive;

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Max Width Container */}
      <div className="max-w-5xl w-full mx-auto h-full flex flex-col bg-white shadow-2xl">
        {/* Header - WhatsApp Style */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg">
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-3 flex-1">
              {/* Back Button - with inline styles to override global CSS */}
              <button
                onClick={() => navigate('/chat-consultations')}
                className="p-2 hover:bg-teal-700 rounded-full transition-colors"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                <ArrowLeft className="w-5 h-5" style={{ color: 'white' }} />
              </button>

              {/* Doctor Info */}
              <div className="flex items-center gap-3 flex-1">
                <div className="relative">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-teal-600" />
                  </div>
                  {isActive && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate" style={{ color: 'white' }}>
                    {consultation?.doctor_name || 'Doctor'}
                  </h3>
                  <p className="text-xs text-teal-100 truncate">
                    Consultation #{consultation?.id || ''}
                  </p>
                </div>
              </div>
            </div>

            {/* End Consultation Button */}
            {isActive && (
              <button
                onClick={handleEndConsultation}
                disabled={endingConsultation}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {endingConsultation ? 'Ending...' : 'End Consultation'}
              </button>
            )}
          </div>

          {/* Time Restriction Banner */}
          {!canSendMessages && isActive && (
            <div className="bg-orange-500 px-4 py-2 flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4" />
              <span>
                {chatSettings?.restriction_message || 'Chat available 9 AM - 10 PM. You can send messages anytime, replies during available hours.'}
              </span>
            </div>
          )}

          {/* Completed Banner */}
          {!isActive && (
            <div className="bg-gray-500 px-4 py-2 flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>This consultation has been {consultation?.status}</span>
            </div>
          )}
        </div>

        {/* Messages Area - WhatsApp Style */}
        <div
          className="flex-1 overflow-y-auto p-4 space-y-4"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4edda' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundColor: '#e5ddd5',
            position: 'relative',
            zIndex: 0
          }}
        >
        {Object.entries(messageGroups).map(([date, msgs]) => (
          <div key={date}>
            {/* Date Separator */}
            <div className="flex justify-center my-4">
              <div className="bg-white/90 shadow-sm px-4 py-1 rounded-full text-xs font-medium text-gray-600">
                {date}
              </div>
            </div>

            {/* Messages for this date */}
            {msgs.map((message, index) => {
              const isDoctor = message.is_doctor;
              const isOwn = !isDoctor;

              return (
                <div
                  key={message.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}
                >
                  <div
                    className={`max-w-[75%] md:max-w-[60%] rounded-lg shadow-md ${
                      isOwn
                        ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white'
                        : 'bg-white text-gray-800'
                    }`}
                    style={{
                      borderRadius: isOwn ? '10px 10px 2px 10px' : '10px 10px 10px 2px'
                    }}
                  >
                    {/* Sender Name (for doctor messages) */}
                    {isDoctor && (
                      <div className="px-3 pt-2 pb-1">
                        <p className="text-xs font-semibold text-teal-600">
                          {message.sender_name}
                        </p>
                      </div>
                    )}

                    {/* Message Text */}
                    <div className="px-3 py-2">
                      <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                        {message.message}
                      </p>

                      {/* Time and Status */}
                      <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                        isOwn ? 'text-teal-100' : 'text-gray-500'
                      }`}>
                        <span>{formatTime(message.timestamp)}</span>
                        {isOwn && (
                          <CheckCheck className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <div className="bg-white/80 rounded-2xl p-8 text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">Start the conversation</p>
              <p className="text-sm">Send a message to begin your consultation</p>
            </div>
          </div>
        )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - WhatsApp Style */}
        <div style={{
          backgroundColor: '#f3f4f6',
          borderTop: '1px solid #e5e7eb',
          padding: '12px',
          position: 'relative',
          zIndex: 10,
          flexShrink: 0
        }}>
          <form onSubmit={handleSendMessage} style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            position: 'relative',
            zIndex: 11
          }}>
            <div style={{
              flex: 1,
              backgroundColor: 'white',
              borderRadius: '24px',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 12
            }}>
              <input
                type="text"
                ref={messageInputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                placeholder="Type a message..."
                disabled={sending}
                className="chat-input"
                style={{
                  width: '100%',
                  height: '48px',
                  border: 'none',
                  outline: 'none',
                  margin: 0,
                  padding: '0 16px',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  backgroundColor: sending ? '#f3f4f6' : 'white',
                  color: sending ? '#9ca3af' : '#333',
                  borderRadius: '24px',
                  pointerEvents: 'auto',
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  position: 'relative',
                  zIndex: 999
                }}
              />
            </div>

            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="chat-send-btn"
              style={{
                padding: '12px',
                borderRadius: '50%',
                border: 'none',
                cursor: !newMessage.trim() || sending ? 'not-allowed' : 'pointer',
                background: newMessage.trim() && !sending
                  ? 'linear-gradient(to right, #14b8a6, #0d9488)'
                  : '#d1d5db',
                color: 'white',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                margin: 0
              }}
            >
              {sending ? (
                <Loader style={{ width: '24px', height: '24px' }} className="animate-spin" />
              ) : (
                <Send style={{ width: '24px', height: '24px' }} />
              )}
            </button>
          </form>

          {/* Info Text */}
          <p style={{
            fontSize: '12px',
            color: '#6b7280',
            textAlign: 'center',
            marginTop: '8px'
          }}>
            Available 9 AM - 10 PM • Messages delivered instantly
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatConsultation;
